import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Handshake, Building2, GraduationCap, FlaskConical, Mail, CheckCircle } from 'lucide-react';

const Partners = () => {
  const { t } = useLanguage();

  const partnershipTypes = [
    {
      icon: Building2,
      title: t('Pharma Licensing', 'Licenciamiento Farmacéutico'),
      description: t(
        'Early-stage licensing and co-development opportunities for pharmaceutical companies seeking next-generation oncology assets.',
        'Oportunidades de licenciamiento temprano y co-desarrollo para compañías farmacéuticas que buscan activos oncológicos de próxima generación.'
      ),
      color: 'purple',
    },
    {
      icon: GraduationCap,
      title: t('Academic Collaboration', 'Colaboración Académica'),
      description: t(
        'Research partnerships with academic institutions for translational studies, biomarker validation, and scientific excellence.',
        'Alianzas de investigación con instituciones académicas para estudios traslacionales, validación de biomarcadores y excelencia científica.'
      ),
      color: 'blue',
    },
    {
      icon: FlaskConical,
      title: t('CRO Services', 'Servicios CRO'),
      description: t(
        'Specialized CRO partnerships for synthesis, characterization, in vitro/in vivo studies, and regulatory support.',
        'Alianzas con CROs especializados para síntesis, caracterización, estudios in vitro/in vivo y soporte regulatorio.'
      ),
      color: 'green',
    },
    {
      icon: Handshake,
      title: t('Strategic Investors', 'Inversores Estratégicos'),
      description: t(
        'Smart capital from investors with pharmaceutical networks and strategic value beyond funding.',
        'Capital inteligente de inversores con redes farmacéuticas y valor estratégico más allá de la financiación.'
      ),
      color: 'yellow',
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-700 via-purple-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        {/* Background Image - Handshake/Partnership */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_metasigna-biotech/artifacts/bmnylwaj_GtSJ_6nxbZpEQ6lmZJ706.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {t('Strategic Partnership Opportunities', 'Oportunidades de Alianza Estratégica')}
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-100 max-w-4xl">
            {t(
              'Join us in developing next-generation immuno-metabolic oncology therapeutics. Improving survival and adding years of life to oncology patients.',
              'Únase a nosotros en el desarrollo de terapéuticas oncológicas inmuno-metabólicas de próxima generación. Mejorando la supervivencia y añadiendo años de vida a los pacientes oncológicos.'
            )}
          </p>

          {/* Impact Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center">
              <div className="text-2xl font-bold text-[#FFD700]">€20B+</div>
              <div className="text-sm text-gray-200">{t('Market Opportunity', 'Oportunidad de Mercado')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center">
              <div className="text-2xl font-bold text-[#FFD700]">1.2-4.2M</div>
              <div className="text-sm text-gray-200">{t('Life-years gained/year', 'Años de vida ganados/año')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center">
              <div className="text-2xl font-bold text-[#FFD700]">50-70%</div>
              <div className="text-sm text-gray-200">{t('Non-responders addressed', 'No respondedores abordados')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 text-center">
              <div className="text-2xl font-bold text-[#FFD700]">4</div>
              <div className="text-sm text-gray-200">{t('Therapeutic Classes', 'Clases Terapéuticas')}</div>
            </div>
          </div>

          {/* Partnership Types Cards */}
          <div className="grid md:grid-cols-4 gap-4">
            {partnershipTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <Icon size={32} className="text-[#FFD700] mb-2" />
                  <div className="text-sm font-semibold">{type.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Technology */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center text-purple-700">
            {t('Clivamed® Platform Technology', 'Tecnología de Plataforma Clivamed®')}
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              {t(
                'Modular dual-function metabolic ligand platform targeting overexpressed nutrient transporters in 80-90% of tumors. Our Pipeline-in-a-Platform approach enables rapid generation of multiple therapeutic candidates within the same technological framework.',
                'Plataforma modular de ligandos metabólicos de doble función dirigida a transportadores de nutrientes sobreexpresados en 80-90% de tumores. Nuestro enfoque Pipeline-in-a-Platform permite la generación rápida de múltiples candidatos terapéuticos dentro del mismo marco tecnológico.'
              )}
            </p>

            {/* Three-Component Architecture */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-8 mb-8">
              <h3 className="text-xl font-semibold mb-6 text-purple-700">
                {t('Three-Component Architecture', 'Arquitectura de Tres Componentes')}
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                  <h4 className="font-semibold text-purple-700 mb-2">
                    {t('Dual-Function Metabolic Targeting Ligand', 'Ligando de Direccionamiento Metabólico de Doble Función')}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {t('Therapeutic ligands for tumor-selective entry and metabolic disruption', 'Ligandos terapéuticos para entrada selectiva tumoral y disrupción metabólica')}
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">2</span>
                  </div>
                  <h4 className="font-semibold text-purple-700 mb-2">
                    {t('Cleavable Linker', 'Conector Escindible')}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {t(
                      'Stable in circulation, activated in tumor microenvironment',
                      'Estable en circulación, activado en microambiente tumoral'
                    )}
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">3</span>
                  </div>
                  <h4 className="font-semibold text-purple-700 mb-2">
                    {t('Therapeutic Payload', 'Carga Terapéutica')}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {t('Modular component enabling multiple candidates', 'Componente modular permitiendo múltiples candidatos')}
                  </p>
                </div>
              </div>
            </div>

            {/* Validated Precedent */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h4 className="font-semibold text-purple-700 mb-3">
                {t('Validated Precedent: FDG-PET', 'Precedente Validado: FDG-PET')}
              </h4>
              <p className="text-gray-700 leading-relaxed">
                {t(
                  'Metabolic pathway imaging (FDG-PET) demonstrates tumor-selective accumulation via metabolic transporter targeting for 50+ years (40+ million procedures annually). This validates the core principle that metabolic vulnerabilities can be exploited for selective uptake in humans. Our investigational therapeutic platform builds upon this validated concept.',
                  'Imágenes de vías metabólicas (FDG-PET) demuestran acumulación selectiva tumoral mediante direccionamiento de transportadores metabólicos durante más de 50 años (más de 40 millones de procedimientos anuales). Esto valida el principio central de que las vulnerabilidades metabólicas pueden ser explotadas para captación selectiva en humanos. Nuestra plataforma terapéutica investigacional se basa en este concepto validado.'
                )}
              </p>
            </div>

            {/* Regulatory Notice Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-amber-800">
                <span className="font-semibold">{t('Regulatory Status:', 'Estado Regulatorio:')}</span>{' '}
                {t(
                  'All Metasigna products are investigational and in preclinical development. The Clivamed® platform technology requires validation through ongoing preclinical studies.',
                  'Todos los productos de Metasigna son investigacionales y están en desarrollo preclínico. La tecnología de plataforma Clivamed® requiere validación a través de estudios preclínicos en curso.'
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Licensing Opportunities */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-purple-700">
            {t('Licensing & Co-Development Opportunities', 'Oportunidades de Licenciamiento y Co-Desarrollo')}
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-2xl font-semibold mb-6 text-purple-700">
                {t('Four Therapeutic Classes', 'Cuatro Clases Terapéuticas')}
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">
                      {t('Checkpoint Inhibitor Conjugates', 'Conjugados de Inhibidores Checkpoint')}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('€14.4B market - Designed to convert cold tumors to responsive targets', '€14.4B mercado - Diseñados para convertir tumores fríos en dianas respondedoras')}
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">
                      {t('Enhanced Chemotherapeutic Conjugates', 'Conjugados Quimioterapéuticos Mejorados')}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('€2.7B market - Designed to reduce systemic toxicity', '€2.7B mercado - Diseñados para reducir toxicidad sistémica')}
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">
                      {t('Theranostic Conjugates', 'Conjugados Teragnósticos')}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('€600M market - Diagnostic + Therapeutic in one molecule', '€600M mercado - Diagnóstico + Terapéutico en una molécula')}
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-purple-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-gray-800">
                      {t('Repositioned Small Molecules', 'Moléculas Pequeñas Reposicionadas')}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('Accelerated pathway with known safety profiles', 'Vía acelerada con perfiles de seguridad conocidos')}
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-2xl font-semibold mb-6 text-purple-700">
                {t('Deal Structure Options', 'Opciones de Estructura de Acuerdo')}
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    {t('Regional Licenses', 'Licencias Regionales')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t(
                      'Exclusive or non-exclusive rights for specific geographic territories (EU, US, Asia, etc.)',
                      'Derechos exclusivos o no exclusivos para territorios geográficos específicos (UE, EE.UU., Asia, etc.)'
                    )}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    {t('Indication-Specific Licenses', 'Licencias Específicas por Indicación')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t(
                      'Rights for specific cancer types or patient populations',
                      'Derechos para tipos de cáncer específicos o poblaciones de pacientes'
                    )}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    {t('Co-Development Agreements', 'Acuerdos de Co-Desarrollo')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t(
                      'Shared risk/reward partnerships with combined expertise and resources',
                      'Alianzas de riesgo/recompensa compartida con experiencia y recursos combinados'
                    )}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    {t('Platform Technology License', 'Licencia de Tecnología de Plataforma')}
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {t(
                      'Access to conjugate partner compounds using our Clivamed® technology',
                      'Acceso para conjugar compuestos asociados utilizando nuestra tecnología Clivamed®'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What We're Looking For */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-8">
            <h3 className="text-xl font-semibold mb-6 text-purple-700">
              {t('What We\'re Looking For in Partners', 'Qué Buscamos en Socios')}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">✓</span>{' '}
                  {t('Clinical development capabilities', 'Capacidades de desarrollo clínico')}
                </p>
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">✓</span>{' '}
                  {t('Regulatory experience', 'Experiencia regulatoria')}
                </p>
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">✓</span>{' '}
                  {t('Commercial infrastructure', 'Infraestructura comercial')}
                </p>
              </div>
              <div>
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">✓</span>{' '}
                  {t('Cultural fit and shared values', 'Ajuste cultural y valores compartidos')}
                </p>
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">✓</span>{' '}
                  {t('Fair deal terms', 'Términos justos de acuerdo')}
                </p>
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">✓</span>{' '}
                  {t('Long-term strategic vision', 'Visión estratégica a largo plazo')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Partners */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-purple-700">
            {t('Current Strategic Partners', 'Socios Estratégicos Actuales')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 text-purple-700">
                {t('University of Málaga (UMA)', 'Universidad de Málaga (UMA)')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Collaborative research agreement for preclinical validation studies. Access to chemistry department for synthesis and characterization.',
                  'Acuerdo de investigación colaborativa para estudios de validación preclínica. Acceso al departamento de química para síntesis y caracterización.'
                )}
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 text-purple-700">
                {t('Cancer Research Center (CIC) Salamanca', 'Centro de Investigación del Cáncer (CIC) Salamanca')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Strategic collaboration with one of Spain\'s reference centers in oncology research.',
                  'Colaboración estratégica con uno de los centros de referencia en investigación oncológica de España.'
                )}
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold mb-4 text-purple-700">
                {t('Academic Teaching Institutions', 'Instituciones Académicas Docentes')}
              </h3>
              <p className="text-gray-600">
                {t(
                  'Guest lectures at UAB, University of Sevilla, Rey Juan Carlos - providing academic validation and future collaboration opportunities.',
                  'Conferencias invitadas en UAB, Universidad de Sevilla, Rey Juan Carlos - proporcionando validación académica y oportunidades de colaboración futura.'
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact BD */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-purple-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Mail size={48} className="mx-auto mb-6 text-[#FFD700]" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('Business Development Contact', 'Contacto de Desarrollo de Negocios')}
          </h2>
          <p className="text-lg mb-8 text-gray-100">
            {t(
              'Interested in exploring partnership opportunities? We respond within 48 hours.',
              '¿Interesado en explorar oportunidades de asociación? Respondemos en 48 horas.'
            )}
          </p>
          <div className="space-y-4 text-gray-100">
            <p>
              <span className="font-semibold">Dr. Ángel Norro</span> - {t('Founder & CEO', 'Fundador y CEO')}
            </p>
            <p>
              <a href="mailto:partners@metasigna.company" className="text-[#FFD700] hover:text-yellow-300">
                partners@metasigna.company
              </a>
            </p>
            <p className="text-sm">Järvevana tee 9, 11314, Tallinn, Estonia</p>
          </div>
          <div className="mt-8">
            <p className="text-sm text-gray-200">
              {t(
                'Bilateral NDAs available upon request. All conversations are confidential.',
                'NDAs bilaterales disponibles bajo solicitud. Todas las conversaciones son confidenciales.'
              )}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
